import { useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState, useLayoutEffect } from "react";

import type { RecordsFiltersDTO } from "@/application/records/dtos/RecordListItemDTO";
import type { MarkdownEndpoint } from "@/application/records/ports/RecordsGatewayPort";
import type { RecordsApplicationService } from "@/application/records/services/RecordsApplicationService";
import { useFrontendContainer } from "@/infrastructure/di/FrontendProvider";
import { auth } from "@/lib/firebase";
import type { PaginatedRecordsResponse } from "@/lib/recordsApi";
import type {
  DetailedReportResponse,
  RecordBatchMetaResponse,
  RecordMetaResponse,
  TranscriptUtterance,
} from "@/lib/types";
import { useRecordsRootStore } from "@/stores/records/createRecordsRootStore";
import type { RecordsListQueryResult } from "@/stores/records/RecordsListStore";
import type { User } from "firebase/auth";
import { useMobxSnapshot } from "./utils/useMobxSnapshot";
import { projectRecordsKeys } from "./project-records-keys";
import { invalidateAskProjectHoverCache } from "@/lib/ask-project-hover-cache";

/** Reserved path segment for "records without project" page – not a record ID */
const RECORDS_WITHOUT_PROJECT_SEGMENT = "without-project";

// Сохраняем старые ключи для совместимости с существующим кодом.
export const recordsKeys = {
  all: ["records"] as const,
  lists: () => [...recordsKeys.all, "list"] as const,
  list: (filters: Record<string, unknown>) =>
    [...recordsKeys.lists(), filters] as const,
  details: () => [...recordsKeys.all, "details"] as const,
  detail: (id: string, version?: number | null) =>
    [...recordsKeys.details(), id, version ?? "latest"] as const,
  meta: (id: string, include?: string[]) =>
    [...recordsKeys.all, "meta", id, include ?? []] as const,
  markdown: (
    id: string,
    reportType: string,
    version?: number | null,
    scope: "private" | "public" = "private"
  ) =>
    [
      ...recordsKeys.details(),
      "markdown",
      scope,
      reportType,
      id,
      version ?? "latest",
    ] as const,
};

export function useRecordsService(): RecordsApplicationService {
  const container = useFrontendContainer();
  return container.resolveRecordsApplicationService();
}

// =============================
// Helpers
// =============================

interface QuerySnapshot<TData> {
  data: TData | undefined;
  error: Error | null;
  isLoading: boolean;
  isFetching: boolean;
  isFetched: boolean;
  refetch: () => Promise<void>;
  dataUpdatedAt?: number;
  errorUpdatedAt?: number;
}

type MutableUseQueryResult<TData> = {
  -readonly [K in keyof UseQueryResult<TData, Error>]: UseQueryResult<
    TData,
    Error
  >[K];
} & {
  fetchFailureCount: number;
  refetch: UseQueryResult<TData, Error>["refetch"];
};

const noop = () => {};

function createEmptyQueryResult<TData>(): MutableUseQueryResult<TData> {
  return {
    data: undefined,
    error: null,
    status: "pending",
    fetchStatus: "idle",
    isLoading: false,
    isFetching: false,
    isSuccess: false,
    isError: false,
    isPending: false,
    isFetched: false,
    isFetchedAfterMount: false,
    isRefetching: false,
    isPlaceholderData: false,
    isInitialLoading: false,
    failureCount: 0,
    fetchFailureCount: 0,
    errorUpdateCount: 0,
    dataUpdatedAt: 0,
    errorUpdatedAt: 0,
    isPaused: false,
    refetch: async () => {},
    remove: noop,
    suspense: () => {
      throw new Error(
        "Suspense is not supported in use-records query adapters"
      );
    },
    isRefetchError: false,
    isStale: true,
  } as unknown as MutableUseQueryResult<TData>;
}

function buildQueryResult<TData>(
  snapshot: QuerySnapshot<TData>,
  previous: MutableUseQueryResult<TData>,
): MutableUseQueryResult<TData> {
  const result = { ...previous };
  const previousError = result.error;
  const previousFailureCount = result.failureCount ?? 0;
  const previousErrorUpdateCount = result.errorUpdateCount ?? 0;
  const previousErrorTimestamp = result.errorUpdatedAt ?? 0;

  const finalStatus: "pending" | "success" | "error" = snapshot.error
    ? "error"
    : snapshot.isFetched
    ? "success"
    : "pending";

  result.data = snapshot.data;
  result.error = snapshot.error;
  result.status = finalStatus;
  result.fetchStatus = snapshot.isFetching ? "fetching" : "idle";
  result.isLoading = snapshot.isLoading;
  result.isFetching = snapshot.isFetching;
  result.isSuccess = finalStatus === "success";
  result.isError = finalStatus === "error";
  result.isPending = finalStatus === "pending";
  result.isFetched = snapshot.isFetched;
  result.isFetchedAfterMount = snapshot.isFetched;
  result.isRefetching = snapshot.isFetching && snapshot.isFetched;
  result.isInitialLoading = snapshot.isLoading && !snapshot.isFetched;

  if (snapshot.error) {
    result.failureCount =
      previousError === snapshot.error
        ? previousFailureCount
        : previousFailureCount + 1;
    result.fetchFailureCount = result.failureCount;
  } else {
    result.failureCount = 0;
    result.fetchFailureCount = 0;
  }

  result.dataUpdatedAt = snapshot.dataUpdatedAt ?? 0;
  if (snapshot.error) {
    const providedTimestamp = snapshot.errorUpdatedAt ?? snapshot.dataUpdatedAt ?? 0;
    result.errorUpdatedAt =
      previousError === snapshot.error && previousErrorTimestamp
        ? previousErrorTimestamp
        : providedTimestamp;
    result.errorUpdateCount =
      previousError === snapshot.error
        ? previousErrorUpdateCount
        : previousErrorUpdateCount + 1;
  } else {
    result.errorUpdatedAt = 0;
    result.errorUpdateCount = 0;
  }

  result.isPaused = false;
  result.isRefetchError = false;
  result.isPlaceholderData = false;
  result.isStale = !snapshot.isFetched || snapshot.isFetching;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  result.refetch = async (_options?: unknown) => {
    await snapshot.refetch();
    return result as UseQueryResult<TData, Error>;
  };

  return result;
}

function useStableQueryResult<TData>(snapshot: QuerySnapshot<TData>): UseQueryResult<TData, Error> {
  const latest = useRef<MutableUseQueryResult<TData> | undefined>(undefined);
  const [cached, setCached] = useState(() => ({
    snapshot,
    result: buildQueryResult(snapshot, createEmptyQueryResult<TData>()),
  }));
  if (cached.snapshot !== snapshot) {
    setCached({ snapshot, result: buildQueryResult(snapshot, cached.result) });
  }
  const result = useMemo(() => ({ ...cached.result, refetch: async () => {
    await snapshot.refetch();
    return (latest.current ?? cached.result) as UseQueryResult<TData, Error>;
  } }), [cached.result, snapshot]);
  useLayoutEffect(() => { latest.current = result; }, [result]);
  return result as UseQueryResult<TData, Error>;
}

function toVersionKey(version?: number | null): number | null {
  if (version === undefined) {
    return null;
  }
  return version;
}

// =============================
// Records list hooks
// =============================

function useAuthFlag(): boolean {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(
    Boolean(auth?.currentUser)
  );

  useEffect(() => {
    if (!auth) return;
    const unsubscribe = auth.onAuthStateChanged((user: User | null) => {
      setIsAuthenticated(Boolean(user));
    });
    return unsubscribe;
  }, []);

  return isAuthenticated;
}

function normalizeFilters(filters: RecordsFiltersDTO): RecordsFiltersDTO {
  const normalized: RecordsFiltersDTO = {};

  if (filters.search && filters.search.trim()) {
    normalized.search = filters.search.trim();
  }

  if (filters.status && filters.status.length > 0) {
    normalized.status = filters.status
      .filter((value) => Boolean(value))
      .map((value) => value!);
  }

  if (filters.type && filters.type.length > 0) {
    normalized.type = Array.from(
      new Set(
        filters.type
          .filter((value) => Boolean(value))
          .map((value) => value!.toLowerCase())
      )
    );
  }

  if (filters.archived !== undefined) {
    normalized.archived = filters.archived;
  }

  if (filters.email && filters.email.trim()) {
    normalized.email = filters.email.trim();
  }

  return normalized;
}

function buildFiltersKey(filters: RecordsFiltersDTO): string {
  const sortable: RecordsFiltersDTO = { ...filters };
  if (sortable.status) {
    sortable.status = [...sortable.status].sort();
  }
  if (sortable.type) {
    sortable.type = [...sortable.type].sort();
  }
  return JSON.stringify(sortable);
}

function useInfiniteRecordsQuery(
  limit: number,
  enabled: boolean,
  mode: "user" | "admin",
  filters: RecordsFiltersDTO = {}
): RecordsListQueryResult {
  const store = useRecordsRootStore();
  const isAuthenticated = useAuthFlag();
  const refreshVersion = store.refreshVersion;
  const normalizedFiltersKey = JSON.stringify(normalizeFilters(filters));
  const normalizedFilters = useMemo(() => JSON.parse(normalizedFiltersKey) as RecordsFiltersDTO, [normalizedFiltersKey]);
  const filtersKey = useMemo(
    () => buildFiltersKey(normalizedFilters),
    [normalizedFilters]
  );

  useEffect(() => {
    const shouldLoad = enabled && isAuthenticated;
    if (!shouldLoad) {
      return;
    }

    if (mode === "user") {
      store
        .ensureUserRecords(limit, true, normalizedFilters)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .catch((_error) => {});
    } else {
      store
        .ensureAdminRecords(limit, true, normalizedFilters)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .catch((_error) => {});
    }
  }, [store, limit, isAuthenticated, mode, enabled, refreshVersion, filtersKey, normalizedFilters]);

  // Query result from store - reactivity handled by observer() on component
  const query =
    mode === "user"
      ? store.getUserRecordsQuery(limit)
      : store.getAdminRecordsQuery(limit);

  const isActive = enabled && isAuthenticated;

  const fetchNextPage = async () => {
    if (!isActive) {
      return;
    }
    await query.fetchNextPage();
  };

  const refetch = async () => {
    if (!isActive) {
      return;
    }
    if (mode === "user") {
      await store.refetchUserRecords(limit);
    } else {
      await store.refetchAdminRecords(limit);
    }
  };

  return {
    data: query.data,
    isLoading: isActive ? query.isLoading : false,
    isFetchingNextPage: query.isFetchingNextPage,
    error: query.error,
    hasNextPage: query.hasNextPage,
    fetchNextPage,
    refetch,
    total: query.total,
    filters: query.filters,
  };
}

export function useRecordsInfinite(
  limit: number = 10,
  enabled: boolean = true,
  filters: RecordsFiltersDTO = {}
): RecordsListQueryResult {
  return useInfiniteRecordsQuery(limit, enabled, "user", filters);
}

export function useAdminRecordsInfinite(
  limit: number = 10,
  enabled: boolean = true,
  filters: RecordsFiltersDTO = {}
): RecordsListQueryResult {
  return useInfiniteRecordsQuery(limit, enabled, "admin", filters);
}

// =============================
// Record details hooks
// =============================

interface UseRecordDetailsOptions<TData> {
  select?: (data: DetailedReportResponse) => TData;
  enabled?: boolean;
}

function useRecordDetailsBase<TData = DetailedReportResponse>(
  recordId: string,
  version?: number | null,
  options?: UseRecordDetailsOptions<TData>
): UseQueryResult<TData, Error> {
  const store = useRecordsRootStore();
  const pathname = usePathname();
  const versionKey = toVersionKey(version);
  const enabled = options?.enabled ?? true;

  // Check if this is a public route or reserved "without-project" segment (not a record ID)
  const isPublicRoute = pathname?.startsWith("/space/records/public/");
  const isReservedSegment = recordId === RECORDS_WITHOUT_PROJECT_SEGMENT;

  // Override enabled to false for public routes and reserved segments
  const effectiveEnabled = enabled && !isPublicRoute && !isReservedSegment;

  useEffect(() => {
    if (!recordId || !effectiveEnabled) {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    store.loadRecordDetails(recordId, versionKey).catch((_error) => {});
  }, [store, recordId, versionKey, effectiveEnabled]);

  const refetch = useCallback(async () => {
    if (!recordId || isPublicRoute) {
      return;
    }
    await store.loadRecordDetails(recordId, versionKey, { force: true });
  }, [store, recordId, versionKey, isPublicRoute]);

  const snapshot = useMobxSnapshot<QuerySnapshot<TData>>(() => {
    if (!recordId) {
      return {
        data: undefined,
        error: null,
        isLoading: false,
        isFetching: false,
        isFetched: false,
        refetch,
        dataUpdatedAt: 0,
      } satisfies QuerySnapshot<TData>;
    }

    const entry = store.getRecordDetailsEntry(recordId, versionKey);
    const source = entry.data ?? null;

    const data = source
      ? options?.select
        ? options.select(source)
        : (source as unknown as TData)
      : undefined;

    const error = entry.error ? new Error(entry.error) : null;
    const isFetched = Boolean(entry.loadedAt);

    return {
      data,
      error,
      isLoading: entry.isLoading && !isFetched,
      isFetching: entry.isLoading,
      isFetched,
      refetch,
      dataUpdatedAt: entry.loadedAt ?? 0,
      errorUpdatedAt: entry.error ? entry.loadedAt ?? Date.now() : undefined,
    } satisfies QuerySnapshot<TData>;
  }, [store, recordId, versionKey]);

  return useStableQueryResult(snapshot);
}

export function useRecordDetails(
  recordId: string,
  version?: number | null,
  skipQuery: boolean = false
) {
  return useRecordDetailsBase<DetailedReportResponse>(recordId, version, {
    enabled: !skipQuery,
  });
}

export interface UseMarkdownReportOptions {
  enabled?: boolean;
  onNotFound?: () => void;
}

export function useMarkdownReport(
  recordId: string,
  reportType: "qa" | "persona" | "custom" | "jtbd",
  version?: number | null,
  options?: UseMarkdownReportOptions
): UseQueryResult<string | null, Error> {
  const endpointMap: Record<typeof reportType, MarkdownEndpoint> = {
    qa: "qa-report",
    persona: "persona-report",
    custom: "custom-report",
    jtbd: "jtbd-markdown-report",
  };

  const endpoint = endpointMap[reportType];

  const store = useRecordsRootStore();
  const versionKey = toVersionKey(version);
  const enabled = options?.enabled ?? true;
  const onNotFound = options?.onNotFound;
  const effectiveMarkdownEnabled =
    enabled && recordId !== RECORDS_WITHOUT_PROJECT_SEGMENT;

  const refetch = useCallback(async () => {
    if (!recordId || recordId === RECORDS_WITHOUT_PROJECT_SEGMENT) {
      return;
    }
    await store.loadRecordMarkdown(recordId, endpoint, versionKey, {
      force: true,
    });
  }, [store, recordId, endpoint, versionKey]);

  useEffect(() => {
    if (!recordId || !effectiveMarkdownEnabled) {
      return;
    }
    void store
      .loadRecordMarkdown(recordId, endpoint, versionKey)
      .catch((error) => {
        if (
          onNotFound &&
          typeof error === "object" &&
          error !== null &&
          "status" in error &&
          (error as { status?: number }).status === 404
        ) {
          onNotFound();
        }
      });
    // Depend on primitive/stable callbacks only — an inline `options` object
    // from callers would re-fire this effect on every render.
  }, [
    store,
    recordId,
    endpoint,
    versionKey,
    effectiveMarkdownEnabled,
    onNotFound,
  ]);

  const snapshot = useMobxSnapshot<QuerySnapshot<string | null>>(() => {
    if (!recordId) {
      return {
        data: undefined,
        error: null,
        isLoading: false,
        isFetching: false,
        isFetched: false,
        refetch,
        dataUpdatedAt: 0,
      } satisfies QuerySnapshot<string | null>;
    }

    const entry = store.getRecordMarkdownEntry(recordId, endpoint, versionKey);

    const error = entry.error ? new Error(entry.error) : null;
    const isFetched = entry.markdown !== null || entry.error !== null;

    return {
      data: entry.markdown,
      error,
      isLoading: entry.isLoading && !isFetched,
      isFetching: entry.isLoading,
      isFetched,
      refetch,
      dataUpdatedAt: entry.loadedAt ?? 0,
      errorUpdatedAt: entry.error ? entry.loadedAt ?? Date.now() : undefined,
    } satisfies QuerySnapshot<string | null>;
  }, [store, recordId, endpoint, versionKey]);

  return useStableQueryResult(snapshot);
}

export function useRecordVersions(
  recordId: string,
  uploadTask?: { status: string }
) {
  const shouldSkip = uploadTask?.status === "uploading";
  const {
    data: details,
    isLoading,
    error,
  } = useRecordDetails(recordId, undefined, shouldSkip);

  const versions = useMemo(() => {
    return details?.available_versions ?? {};
  }, [details?.available_versions]);

  const maxVersion = useMemo(() => {
    const values = Object.values(versions ?? {}).flat();
    if (!values.length) return null;
    return Math.max(...values);
  }, [versions]);

  return {
    versions,
    maxVersion,
    isLoading,
    error,
  };
}

// =============================
// Metadata hooks
// =============================

export function useRecordMeta(
  recordId: string,
  includeInput?: string[],
  enabled: boolean = true
): UseQueryResult<RecordMetaResponse, Error> {
  const store = useRecordsRootStore();
  const includeKey = useMemo(
    () => (includeInput ? JSON.stringify(includeInput) : "__none__"),
    [includeInput]
  );

  const include = useMemo(() => includeKey === "__none__" ? undefined : JSON.parse(includeKey) as string[], [includeKey]);
  const isReservedSegment = recordId === RECORDS_WITHOUT_PROJECT_SEGMENT;
  const effectiveMetaEnabled = enabled && !isReservedSegment;

  useEffect(() => {
    if (!recordId || !effectiveMetaEnabled) {
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    store.loadRecordMeta(recordId, include).catch((_error) => {});
  }, [store, recordId, includeKey, effectiveMetaEnabled, include]);

  const refetch = useCallback(async () => {
    if (!recordId || recordId === RECORDS_WITHOUT_PROJECT_SEGMENT) {
      return;
    }
    await store.loadRecordMeta(recordId, include);
  }, [recordId, store, include]);

  const snapshot = useMobxSnapshot<QuerySnapshot<RecordMetaResponse>>(() => {
    if (!recordId || isReservedSegment) {
      return {
        data: undefined,
        error: null,
        isLoading: false,
        isFetching: false,
        isFetched: false,
        refetch,
        dataUpdatedAt: 0,
      } satisfies QuerySnapshot<RecordMetaResponse>;
    }

    const entry = store.getRecordMetaEntry(recordId, include);
    const error = entry.error ? new Error(entry.error) : null;
    // Fix: Use loadedAt to determine if fetch attempt completed (success or error)
    const isFetched = Boolean(entry.loadedAt);

    return {
      data: entry.data ?? undefined,
      error,
      isLoading: entry.isLoading && !isFetched,
      isFetching: entry.isLoading,
      isFetched,
      refetch,
      dataUpdatedAt: entry.loadedAt ?? 0,
      errorUpdatedAt: entry.error ? entry.loadedAt ?? Date.now() : undefined,
    } satisfies QuerySnapshot<RecordMetaResponse>;
  }, [store, recordId, includeKey]);

  return useStableQueryResult(snapshot);
}

export function useRecordsMetaBatch(
  recordIds: string[],
  includeInput?: string[],
  enabled: boolean = true
): UseQueryResult<Record<string, RecordBatchMetaResponse>, Error> {
  const store = useRecordsRootStore();
  const sortedIdsKey = JSON.stringify([...recordIds].sort());
  const sortedIds = useMemo(() => JSON.parse(sortedIdsKey) as string[], [sortedIdsKey]);
  const includeKey = useMemo(
    () => (includeInput ? JSON.stringify(includeInput) : "__none__"),
    [includeInput]
  );

  const include = useMemo(() => includeKey === "__none__" ? undefined : JSON.parse(includeKey) as string[], [includeKey]);
  useEffect(() => {
    if (!enabled || sortedIds.length === 0) {
      return;
    }
    store
      .loadRecordsMetaBatch(sortedIds, { include })
      .catch((_error) => {
  void _error;});
  }, [store, sortedIdsKey, includeKey, enabled, sortedIds, include]);

  const refetch = useCallback(async () => {
    if (!sortedIds.length) {
      return;
    }
    await store.loadRecordsMetaBatch(sortedIds, { include });
  }, [sortedIds, store, include]);

  const snapshot = useMobxSnapshot<
    QuerySnapshot<Record<string, RecordBatchMetaResponse>>
  >(() => {
    if (!sortedIds.length) {
      return {
        data: {},
        error: null,
        isLoading: false,
        isFetching: false,
        isFetched: true,
        refetch,
        dataUpdatedAt: 0,
      } satisfies QuerySnapshot<Record<string, RecordBatchMetaResponse>>;
    }

    const entry = store.getRecordsMetaBatchEntry(sortedIds, include);
    const error = entry.error ? new Error(entry.error) : null;
    const isFetched = Boolean(entry.data);

    return {
      data: entry.data ?? undefined,
      error,
      isLoading: entry.isLoading && !isFetched,
      isFetching: entry.isLoading,
      isFetched,
      refetch,
      dataUpdatedAt: entry.loadedAt ?? 0,
      errorUpdatedAt: entry.error ? entry.loadedAt ?? Date.now() : undefined,
    } satisfies QuerySnapshot<Record<string, RecordBatchMetaResponse>>;
  }, [store, sortedIdsKey, includeKey]);

  const finalResult = useStableQueryResult(snapshot);

  return finalResult;
}

export function useTranscript(
  recordId: string,
  version?: number | null,
  options?: { enabled?: boolean }
) {
  return useRecordDetailsBase<TranscriptUtterance[]>(recordId, version, {
    enabled: options?.enabled ?? true,
    select: (data) => data.transcripts ?? [],
  });
}

export function useSetupData(
  recordId: string,
  version?: number | null,
  options?: { enabled?: boolean }
) {
  return useRecordDetailsBase(recordId, version, {
    enabled: options?.enabled ?? true,
    select: (data) => data.setup ?? null,
  });
}

// =============================
// Mutations
// =============================

export function useRecordsMutations() {
  const store = useRecordsRootStore();
  const queryClient = useQueryClient();

  const invalidateProjectRecordsCaches = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: projectRecordsKeys.all,
      exact: false,
    });
    invalidateAskProjectHoverCache();
  }, [queryClient]);

  const mutationState = {
    isDeleting: store.mutationsStore.isDeleting,
    deleteError: store.mutationsStore.deleteError,
    isArchiving: store.mutationsStore.isArchiving,
    archiveError: store.mutationsStore.archiveError,
    isRenaming: store.mutationsStore.isRenaming,
    renameError: store.mutationsStore.renameError,
    isRemovingFromProject: store.mutationsStore.isRemovingFromProject,
    removeFromProjectError: store.mutationsStore.removeFromProjectError,
    isAssigningToProject: store.mutationsStore.isAssigningToProject,
    assignToProjectError: store.mutationsStore.assignToProjectError,
    isChangingStatus: store.mutationsStore.isChangingStatus,
    changeStatusError: store.mutationsStore.changeStatusError,
  };

  const deleteRecords = async (recordIds: string[]) => {
    await store.mutationsStore.deleteRecords(recordIds);
    await invalidateProjectRecordsCaches();
  };

  const archiveRecords = async (
    recordIds: string[],
    isUnarchive: boolean = false
  ) => {
    await store.mutationsStore.archiveRecords(recordIds, isUnarchive);
    await invalidateProjectRecordsCaches();
  };

  const renameRecords = async ({
    recordId,
    newName,
  }: {
    recordId: string;
    newName: string;
  }) => {
    await store.mutationsStore.renameRecord(recordId, newName);
    await invalidateProjectRecordsCaches();
  };

  const removeFromProject = async (recordIds: string[], projectId?: string) => {
    await store.mutationsStore.removeFromProject(recordIds, projectId);
    await invalidateProjectRecordsCaches();
  };

  const assignToProject = async (
    recordIds: string[],
    projectId: string,
    projectName: string
  ) => {
    await store.mutationsStore.assignToProject(
      recordIds,
      projectId,
      projectName
    );
    await invalidateProjectRecordsCaches();
  };

  const changeRecordStatus = async (recordId: string, status: string) => {
    await store.mutationsStore.changeRecordStatus(recordId, status);
    await invalidateProjectRecordsCaches();
  };

  const invalidateRecords = () => {
    store.invalidateRecords();
  };

  const invalidateRecordReports = (recordId: string) => {
    store.invalidateRecordDetails(recordId);
  };

  const refreshRecordMeta = async (recordId: string): Promise<void> => {
    await store.refreshRecordMeta(recordId);
  };

  const prefetchAllReports = async (
    recordId: string,
    availableVersions: Record<string, number[]>
  ) => {
    if (!recordId || !availableVersions) {
      return;
    }

    const latestVersions = new Set<number | null>();
    Object.values(availableVersions).forEach((versions) => {
      if (!versions?.length) {
        return;
      }
      latestVersions.add(versions[versions.length - 1] ?? null);
    });

    await Promise.all(
      Array.from(latestVersions).map((version) =>
        store.loadRecordDetails(recordId, version ?? null, {
          force: true,
          background: true,
        })
      )
    );
  };

  return {
    invalidateRecords,
    invalidateRecordReports,
    refreshRecordMeta,
    prefetchAllReports,
    deleteRecords,
    isDeleting: mutationState.isDeleting,
    deleteError: mutationState.deleteError,
    archiveRecords,
    isArchiving: mutationState.isArchiving,
    archiveError: mutationState.archiveError,
    renameRecords,
    isRenaming: mutationState.isRenaming,
    renameError: mutationState.renameError,
    removeFromProject,
    isRemovingFromProject: mutationState.isRemovingFromProject,
    removeFromProjectError: mutationState.removeFromProjectError,
    assignToProject,
    isAssigningToProject: mutationState.isAssigningToProject,
    assignToProjectError: mutationState.assignToProjectError,
    changeRecordStatus,
    isChangingStatus: mutationState.isChangingStatus,
    changeStatusError: mutationState.changeStatusError,
  };
}

// =============================
// Shared utilities
// =============================

export function useRecordsMetaBatchAndList(
  limit: number,
  enabled: boolean
): {
  query: RecordsListQueryResult;
  metadata: UseQueryResult<Record<string, RecordBatchMetaResponse>, Error>;
} {
  const recordsQuery = useRecordsInfinite(limit, enabled);
  const recordIds = useMemo(() => {
    const pages: PaginatedRecordsResponse[] | undefined =
      recordsQuery.data?.pages;
    const seen = new Set<string>();

    if (!pages) {
      return [] as string[];
    }

    const ids: string[] = [];
    for (const page of pages) {
      for (const record of page.records) {
        if (record?.id && !seen.has(record.id)) {
          seen.add(record.id);
          ids.push(record.id);
        }
      }
    }

    return ids;
  }, [recordsQuery.data?.pages]);

  const metadata = useRecordsMetaBatch(recordIds, undefined, enabled);

  return { query: recordsQuery, metadata };
}
